import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    Paper,
    Divider
} from '@mui/material';
import './CognitivePatternChart.css';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
    ReferenceArea,
    Dot
} from 'recharts';
import { getCurrentCognitivePattern } from '../../services/notificationsService';
import {
    TrendingUp,
    TrendingDown,
    Notifications,
    Info,
    Schedule,
    Coffee,
    Rocket,
    NotificationsActive
} from '@mui/icons-material';
import TourComponent from '../UserGuide/TourComponent';

const CognitivePatternChart = ({ userId }) => {
    const { t, i18n } = useTranslation();
    const [pattern, setPattern] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [runTour, setRunTour] = useState(false);

    // Helper function to identify transition type based on hour and period data
    const getTransitionPeriodType = (hour, adaptive) => {
        if (!adaptive) return null;
        
        const peakPeriods = adaptive.peakPeriods || [];
        const slumpPeriods = adaptive.slumpPeriods || [];
        
        // Check if hour is at the start of any peak period
        for (let i = 0; i < peakPeriods.length; i++) {
            if (hour === peakPeriods[i].startHour) {
                return i === 0 ? 'primaryPeak' : 'secondaryPeak';
            }
        }
        
        // Check if hour is at the start of any slump period
        for (let i = 0; i < slumpPeriods.length; i++) {
            if (hour === slumpPeriods[i].startHour) {
                return 'slump';
            }
        }
        
        return null;
    };

    // Helper function to translate transition messages based on period type
    const translateTransitionMessage = (transition, hour, adaptive) => {
        if (!transition) return '';
        
        const message = transition.message || '';
        const periodType = getTransitionPeriodType(hour, adaptive);
        
        // Check if pattern is emerging and get suffix
        let suffix = '';
        if (message.includes('(pattern emerging)')) {
            suffix = ` ${t('cognitivePattern.patternEmerging')}`;
        }
        
        // Use identified period type for translation
        if (periodType === 'primaryPeak' || periodType === 'secondaryPeak') {
            // Check message content to determine which peak message variant
            if (message.includes('Peak performance time')) {
                return t('cognitivePattern.peakTransitionMessage') + suffix;
            } else {
                return t('cognitivePattern.cognitivepeakStarting') + suffix;
            }
        } else if (periodType === 'slump') {
            // Check message content to determine which slump message variant
            if (message.includes('Low energy period')) {
                return t('cognitivePattern.slumpTransitionMessage') + suffix;
            } else {
                return t('cognitivePattern.naturalDipApproaching') + suffix;
            }
        }
        
        // Fallback: try using transition.type if available
        const type = transition.type;
        if (type === 'peak') {
            if (message.includes('Peak performance time')) {
                return t('cognitivePattern.peakTransitionMessage') + suffix;
            } else {
                return t('cognitivePattern.cognitivepeakStarting') + suffix;
            }
        } else if (type === 'slump') {
            if (message.includes('Low energy period')) {
                return t('cognitivePattern.slumpTransitionMessage') + suffix;
            } else {
                return t('cognitivePattern.naturalDipApproaching') + suffix;
            }
        }
        
        // Final fallback to original message
        return message;
    };

    useEffect(() => {
        if (!userId) return;

        const fetchPattern = async () => {
            try {
                setLoading(true);
                const response = await getCurrentCognitivePattern(userId);
                console.log('Cognitive pattern response:', response); 

                if (response && response.pattern) {
                    console.log("cognitive pattern: ", response.pattern)
                    setPattern(response.pattern);
                    setError(null);
                } else if (response && !response.inObservationPeriod) {
                    setError(t('cognitivePattern.notInObservationPeriod'));
                    setPattern(null);
                } else {
                    setError(t('cognitivePattern.noPatternData'));
                    setPattern(null);
                }
            } catch (err) {
                console.error('Error fetching cognitive pattern:', err);
                setError(t('cognitivePattern.noPatternData'));
                setPattern(null);
            } finally {
                setLoading(false);
            }
        };

        fetchPattern();
    }, [userId, t]);

    if (loading) {
        return (
            <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                </CardContent>
            </Card>
        );
    }

    if (error || !pattern?.metadata?.adaptive) {
        return (
            <Card sx={{ height: '100%' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('cognitivePattern.titleNoPattern')}
                    </Typography>
                    <Alert severity="info">
                        {t('cognitivePattern.noDataMessage')}
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    const adaptive = pattern.metadata.adaptive;
    const hourlyStats = pattern.hourlyStats || [];

    let workStartHour = 9;
    let workEndHour = 17;

    if (adaptive.peakPeriods && adaptive.peakPeriods.length > 0) {
        const allPeriods = [...(adaptive.peakPeriods || []), ...(adaptive.slumpPeriods || [])];
        if (allPeriods.length > 0) {
            workStartHour = Math.min(...allPeriods.map(p => p.startHour));
            workEndHour = Math.max(...allPeriods.map(p => p.endHour));
        }
    }
    
    // Calculate min and max for normalization
    const scores = hourlyStats.filter(s => s.mean).map(s => s.mean);
    const minScore = scores.length > 0 ? Math.min(...scores) : 0;
    const maxScore = scores.length > 0 ? Math.max(...scores) : 10;
    const scoreRange = maxScore - minScore || 1;
    
    // Prepare chart data for 24 hours
    const chartData = [];
    for (let hour = 0; hour < 24; hour++) {
        const stat = hourlyStats.find(s => s.hour === hour);
        const isWorkHour = hour >= workStartHour && hour < workEndHour;
        
        // Find if this hour is a transition point
        const transition = adaptive.transitions?.find(t => t.hour === hour);
        
        // Determine if hour is in peak or slump period
        let periodType = 'normal';
        if (adaptive.peakPeriods?.some(p => hour >= p.startHour && hour < p.endHour)) {
            periodType = 'peak';
        } else if (adaptive.slumpPeriods?.some(p => hour >= p.startHour && hour < p.endHour)) {
            periodType = 'slump';
        }

        let normalizedScore = null;
        if (stat && stat.mean !== null && stat.mean !== undefined) {
            normalizedScore = ((stat.mean - minScore) / scoreRange) * 4 - 2;
        }
        
        chartData.push({
            hour: `${hour.toString().padStart(2, '0')}:00`,
            hourNum: hour,
            score: normalizedScore,
            mean: stat?.mean || null,
            count: stat?.count || 0,
            isWorkHour,
            hasTransition: !!transition,
            transitionMessage: transition?.message || null,
            periodType,
            isCurrentHour: hour === new Date().getHours()
        });
    }

    const CustomDot = (props) => {
        const { cx, cy, payload } = props;
        if (payload.hasTransition && payload.isWorkHour) {
            return (
                <g>
                    <circle cx={cx} cy={cy} r={8} fill="#ff9800" stroke="#fff" strokeWidth={2} />
                    <Notifications x={cx - 6} y={cy - 6} width={12} height={12} fill="#fff" />
                </g>
            );
        }
        if (payload.isCurrentHour) {
            return <circle cx={cx} cy={cy} r={6} fill="#2196f3" stroke="#fff" strokeWidth={2} />;
        }
        return null;
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload[0]) {
            const data = payload[0].payload;
            return (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">{data.hour}</Typography>
                    {data.mean && (
                        <Typography variant="body2">
                            {t('cognitivePattern.averageScore')}: {data.mean.toFixed(1)}
                        </Typography>
                    )}
                    {data.count > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {t('cognitivePattern.dataPoints', { count: data.count })}
                        </Typography>
                    )}
                    {data.hasTransition && (
                        <Box sx={{ mt: 1 }}>
                            <Chip
                                icon={<NotificationsActive />}
                                label={t('cognitivePattern.notificationTime')}
                                size="small"
                                color="warning"
                            />
                        </Box>
                    )}
                    {data.transitionMessage && (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                            {data.transitionMessage}
                        </Typography>
                    )}
                </Paper>
            );
        }
        return null;
    };

    const currentHour = new Date().getHours();

    return (
        <Card key={i18n.language} sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: 2
        }}>
            <CardContent sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontWeight: 600,
                        color: '#333'
                    }}>
                        <Schedule sx={{ color: '#1976d2' }} /> {t('cognitivePattern.title')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('cognitivePattern.aboutChart')}>
                            <IconButton 
                                data-tour="cognitive-pattern-info"
                                size="small" 
                                onClick={() => setShowInfo(!showInfo)}
                                sx={{ 
                                    bgcolor: showInfo ? '#e3f2fd' : 'transparent',
                                    '&:hover': { bgcolor: '#e3f2fd' }
                                }}
                            >
                                <Info />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Info Panel */}
                {showInfo && (
                    <Paper 
                        sx={{ 
                            mb: 3, 
                            p: 3, 
                            bgcolor: '#e3f2fd',
                            borderRadius: 2,
                            border: '1px solid #90caf9'
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            gutterBottom 
                            sx={{ 
                                fontWeight: 600,
                                color: '#1565c0',
                                textAlign: 'center',
                                mb: 2
                            }}
                        >
                            {t('cognitivePattern.howToRead')}
                        </Typography>
                        <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: 2
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography sx={{ color: '#1565c0', fontWeight: 'bold' }}>📈</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('cognitivePattern.chartExplanation')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography sx={{ color: '#1565c0', fontWeight: 'bold' }}>📊</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('cognitivePattern.currentHour')}. {t('cognitivePattern.notifications')}.
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography sx={{ color: '#1565c0', fontWeight: 'bold' }}>🔔</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('cognitivePattern.bellIconsExplanation')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography sx={{ color: '#1565c0', fontWeight: 'bold' }}>🎨</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('cognitivePattern.greenAreas')}. {t('cognitivePattern.orangeAreas')}.
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography sx={{ color: '#1565c0', fontWeight: 'bold' }}>⏰</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('cognitivePattern.nowLineExplanation')}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <IconButton 
                                size="small" 
                                onClick={() => setShowInfo(false)}
                                sx={{ 
                                    color: '#1565c0',
                                    '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' }
                                }}
                            >
                                <Info sx={{ fontSize: 18, mr: 0.5 }} />
                                <Typography variant="caption">{showInfo ? t('cognitivePattern.hideGuide') : t('cognitivePattern.showGuide')}</Typography>
                            </IconButton>
                        </Box>
                    </Paper>
                )}

                {/* Chart */}
                <Box sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }} data-tour="cognitive-pattern-chart">
                    <ResponsiveContainer width="100%" height={400} minWidth={800}>
                        <LineChart data={chartData} margin={{ top: 20, right: 40, left: 20, bottom: 60 }}>
                            <defs>
                                <linearGradient id="colorPeak" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#4caf50" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1}/>
                                </linearGradient>
                                <linearGradient id="colorSlump" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#ff9800" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#ff9800" stopOpacity={0.1}/>
                                </linearGradient>
                                <linearGradient id="colorLine" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor="#1976d2" stopOpacity={0.8}/>
                                    <stop offset="50%" stopColor="#2196f3" stopOpacity={1}/>
                                    <stop offset="100%" stopColor="#1976d2" stopOpacity={0.8}/>
                                </linearGradient>
                            </defs>
                            
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeOpacity={0.5} />
                            
                            {/* Highlight work hours with subtle background */}
                            <ReferenceArea 
                                x1={`${workStartHour.toString().padStart(2, '0')}:00`} 
                                x2={`${workEndHour.toString().padStart(2, '0')}:00`} 
                                fill="#1976d2" 
                                fillOpacity={0.05} 
                            />
                            
                            {/* Mark peak periods with gradient */}
                            {adaptive.peakPeriods?.map((period, idx) => (
                                <ReferenceArea
                                    key={`peak-${idx}`}
                                    x1={`${period.startHour.toString().padStart(2, '0')}:00`}
                                    x2={`${period.endHour.toString().padStart(2, '0')}:00`}
                                    fill="url(#colorPeak)"
                                />
                            ))}
                            
                            {/* Mark slump periods with gradient */}
                            {adaptive.slumpPeriods?.map((period, idx) => (
                                <ReferenceArea
                                    key={`slump-${idx}`}
                                    x1={`${period.startHour.toString().padStart(2, '0')}:00`}
                                    x2={`${period.endHour.toString().padStart(2, '0')}:00`}
                                    fill="url(#colorSlump)"
                                />
                            ))}
                            
                            {/* Current hour line */}
                            <ReferenceLine 
                                x={`${currentHour.toString().padStart(2, '0')}:00`} 
                                stroke="#1976d2" 
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                label={{ 
                                    value: t('cognitivePattern.now'), 
                                    position: "top",
                                    offset: 10,
                                    style: { fill: '#1976d2', fontWeight: 'bold', fontSize: 12 }
                                }}
                            />
                            
                            <XAxis 
                                dataKey="hour" 
                                interval={2}
                                tick={{ fontSize: 11, fill: '#666' }}
                                angle={0}
                                textAnchor="middle"
                                height={50}
                                label={{ 
                                    value: t('cognitivePattern.timeOfDay'), 
                                    position: 'insideBottom', 
                                    offset: -40,
                                    style: { textAnchor: 'middle', fontSize: 12, fill: '#666' }
                                }}
                            />
                            <YAxis 
                                domain={[-2, 2]}
                                ticks={[-2, -1, 0, 1, 2]}
                                tick={{ fontSize: 11, fill: '#666' }}
                                label={{ 
                                    value: 'Performance Level', 
                                    angle: -90, 
                                    position: 'insideLeft',
                                    style: { textAnchor: 'middle', fontSize: 12, fill: '#666' }
                                }}
                                tickFormatter={(value) => {
                                    const labels = { 
                                        '-2': t('cognitivePattern.veryLow'), 
                                        '-1': t('cognitivePattern.low'), 
                                        '0': t('cognitivePattern.normal'), 
                                        '1': t('cognitivePattern.high'), 
                                        '2': t('cognitivePattern.peak') 
                                    };
                                    return labels[value] || '';
                                }}
                            />
                            <RechartsTooltip content={<CustomTooltip />} />
                            
                            <Line
                                type="monotone"
                                dataKey="score"
                                stroke="url(#colorLine)"
                                strokeWidth={3}
                                dot={<CustomDot />}
                                activeDot={{ r: 10, fill: '#1976d2' }}
                                connectNulls={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Legend and Summary */}
                <Box sx={{ mt: 3 }} data-tour="pattern-summary">
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                        {t('cognitivePattern.todaysPatternSummary')}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                        {/* Peak periods */}
                        {adaptive.peakPeriods?.map((period, idx) => (
                            <Chip
                                key={`peak-chip-${idx}`}
                                icon={<Rocket />}
                                label={`${t('cognitivePattern.peak')}: ${period.startHour.toString().padStart(2, '0')}:00 - ${period.endHour.toString().padStart(2, '0')}:00`}
                                sx={{ 
                                    bgcolor: '#e8f5e9',
                                    color: '#2e7d32',
                                    borderColor: '#4caf50',
                                    fontWeight: 500,
                                    '& .MuiChip-icon': { color: '#4caf50' }
                                }}
                                variant="outlined"
                            />
                        ))}
                        
                        {/* Slump periods */}
                        {adaptive.slumpPeriods?.map((period, idx) => (
                            <Chip
                                key={`slump-chip-${idx}`}
                                icon={<Coffee />}
                                label={`${t('cognitivePattern.energySlump')}: ${period.startHour.toString().padStart(2, '0')}:00 - ${period.endHour.toString().padStart(2, '0')}:00`}
                                sx={{ 
                                    bgcolor: '#fff3e0',
                                    color: '#e65100',
                                    borderColor: '#ff9800',
                                    fontWeight: 500,
                                    '& .MuiChip-icon': { color: '#ff9800' }
                                }}
                                variant="outlined"
                            />
                        ))}
                    </Box>

                    {/* Notification schedule */}
                    {adaptive.transitions && adaptive.transitions.length > 0 && (
                        <Box sx={{ mt: 3 }} data-tour="notification-schedule">
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                                {t('cognitivePattern.notificationSchedule')}
                            </Typography>
                            <Box sx={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                                gap: 1.5,
                                mt: 2
                            }}>
                                {adaptive.transitions
                                    .filter(t => t.hour >= workStartHour && t.hour < workEndHour)
                                    .map((transition, idx) => (
                                        <Paper 
                                            key={`${idx}-${i18n.language}`} 
                                            sx={{ 
                                                p: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                bgcolor: transition.hour === currentHour ? '#e3f2fd' : '#fafafa',
                                                border: transition.hour === currentHour ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                borderRadius: 2,
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            <NotificationsActive 
                                                sx={{ 
                                                    fontSize: 24, 
                                                    color: transition.hour === currentHour ? '#1976d2' : '#757575'
                                                }} 
                                            />
                                            <Box flex={1}>
                                                <Typography 
                                                    variant="subtitle2" 
                                                    sx={{ 
                                                        fontWeight: 600,
                                                        color: transition.hour === currentHour ? '#1976d2' : '#424242'
                                                    }}
                                                >
                                                    {transition.hour.toString().padStart(2, '0')}:00
                                                </Typography>
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ color: '#666', mt: 0.5 }}
                                                >
                                                    {translateTransitionMessage(transition, transition.hour, adaptive)}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    ))}
                            </Box>
                        </Box>
                    )}

                    {/* Data info */}
                    <Box sx={{ mt: 3, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t('cognitivePattern.basedOnMeasurements', { total: adaptive.totalHistoricalData || 0 })}
                            {adaptive.recentDataPoints && ` ${t('cognitivePattern.recentMeasurements', { recent: adaptive.recentDataPoints })}`}
                        </Typography>
                    </Box>
                </Box>

                {/* Explanation of what patterns mean */}
                <Box sx={{ 
                    mt: 4, 
                    p: 3, 
                    bgcolor: '#f8f9fa', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                }}>
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 600, 
                            color: '#333',
                            textAlign: 'center',
                            mb: 3
                        }}
                    >
                        {t('cognitivePattern.whatDoesThisMean')}
                    </Typography>
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: 2.5
                    }}>
                        <Paper sx={{ 
                            p: 2.5, 
                            display: 'flex', 
                            gap: 2,
                            alignItems: 'flex-start',
                            bgcolor: 'white',
                            borderRadius: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                            }
                        }}>
                            <Box sx={{ 
                                p: 1.5, 
                                borderRadius: '50%', 
                                bgcolor: '#e8f5e9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Rocket sx={{ fontSize: 24, color: '#4caf50' }} />
                            </Box>
                            <Box flex={1} data-tour="peak-indicator">
                                <Typography variant="subtitle1" fontWeight={600} color="#333">
                                    {t('cognitivePattern.peakPerformance')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {t('cognitivePattern.peakDescription')}
                                </Typography>
                            </Box>
                        </Paper>
                        
                        <Paper sx={{ 
                            p: 2.5, 
                            display: 'flex', 
                            gap: 2,
                            alignItems: 'flex-start',
                            bgcolor: 'white',
                            borderRadius: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                            }
                        }}>
                            <Box sx={{ 
                                p: 1.5, 
                                borderRadius: '50%', 
                                bgcolor: '#fff3e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Coffee sx={{ fontSize: 24, color: '#ff9800' }} />
                            </Box>
                            <Box flex={1} data-tour="slump-indicator">
                                <Typography variant="subtitle1" fontWeight={600} color="#333">
                                    {t('cognitivePattern.energySlump')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {t('cognitivePattern.slumpDescription')}
                                </Typography>
                            </Box>
                        </Paper>
                        
                        <Paper sx={{ 
                            p: 2.5, 
                            display: 'flex', 
                            gap: 2,
                            alignItems: 'flex-start',
                            bgcolor: 'white',
                            borderRadius: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                            }
                        }}>
                            <Box sx={{ 
                                p: 1.5, 
                                borderRadius: '50%', 
                                bgcolor: '#e3f2fd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <NotificationsActive sx={{ fontSize: 24, color: '#1976d2' }} />
                            </Box>
                            <Box flex={1}>
                                <Typography variant="subtitle1" fontWeight={600} color="#333">
                                    {t('cognitivePattern.smartNotifications')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {t('cognitivePattern.smartNotificationsDescription')}
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            </CardContent>
            <TourComponent
                run={runTour}
                setRun={setRunTour}
                tourType="cognitivePattern"
            />
        </Card>
    );
};

export default CognitivePatternChart;